import React from "react";
import { motion } from "framer-motion";
import { FaCheckCircle } from "react-icons/fa";
import { Helmet } from "react-helmet-async";

const About = () => {
  return (
    <section className="bg-gray-50 py-20 px-6 md:px-12 lg:px-20 relative overflow-hidden">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>About Solonet Properties | Premium Real Estate in Ghana</title>
        <meta
          name="description"
          content="Solonet Properties specializes in premium real estate solutions in Ghana. Luxury homes, expert advisory, and trusted property management services."
        />
        <meta name="keywords" content="Solonet Properties, Real Estate Ghana, Luxury Properties Ghana, Property Advisory, Kwame Sarfo" />
        <meta property="og:title" content="About Solonet Properties | Premium Real Estate in Ghana" />
        <meta property="og:description" content="Discover Solonet Properties, providing luxury and trusted real estate solutions in Ghana." />
        <meta property="og:image" content="/images/about-og.jpg" />
        <meta property="og:url" content="https://solonetproperties.com/about" />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* Background Shapes */}
      <motion.div
        className="absolute top-0 -left-20 w-72 h-72 bg-blue-200 rounded-full filter blur-3xl opacity-30"
        animate={{ y: [0, 20, 0] }}
        transition={{ duration: 8, repeat: Infinity }}
      />
      <motion.div
        className="absolute bottom-0 -right-16 w-96 h-96 bg-purple-200 rounded-full filter blur-3xl opacity-25"
        animate={{ y: [0, -30, 0] }}
        transition={{ duration: 10, repeat: Infinity }}
      />

      {/* Content */}
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 items-center relative z-10">
        {/* Left Image Section */}
        <motion.div
          initial={{ opacity: 0, x: -80 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          className="relative w-full h-[450px] rounded-2xl overflow-hidden shadow-lg"
        >
          <img
            src={process.env.PUBLIC_URL + "/images/about.jpg"}
            alt="About Solonet Properties"
            className="w-full h-full object-cover rounded-2xl"
          />
          {/* Blue Overlay */}
          <div className="absolute inset-0 bg-blue-600/20"></div>
        </motion.div>

        {/* Right Content Section */}
        <motion.div
          initial={{ opacity: 0, x: 80 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
            About <span className="text-blue-600">Solonet Properties</span>
          </h2>
          <div className="w-24 h-1 bg-blue-600 mt-3 mb-6 rounded-full"></div>

          <p className="text-gray-600 text-lg leading-relaxed mb-6">
            At Solonet Properties, we connect you with premium real estate solutions in Ghana. 
            Our mission is to deliver luxury, comfort, and value through properties that redefine modern living.
          </p>

          {/* Key Highlights */}
          <ul className="mt-6 space-y-3 text-gray-700">
            <li className="flex items-center gap-3">
              <FaCheckCircle className="text-blue-600 text-lg" />
              Expert real estate consultancy and advisory
            </li>
            <li className="flex items-center gap-3">
              <FaCheckCircle className="text-blue-600 text-lg" />
              Premium luxury properties at unbeatable prices
            </li>
            <li className="flex items-center gap-3">
              <FaCheckCircle className="text-blue-600 text-lg" />
              Trusted by hundreds of satisfied homeowners
            </li>
          </ul>

          {/* CTA Buttons */}
          <div className="flex flex-wrap gap-4 mt-8">
            <motion.a
              href="/contact"
              whileHover={{ scale: 1.05 }}
              className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-3 rounded-lg shadow-lg transition"
            >
              Contact Us
            </motion.a>
            <motion.a
              href="/projects"
              whileHover={{ scale: 1.05 }}
              className="inline-block border-2 border-blue-600 text-blue-600 font-semibold px-8 py-3 rounded-lg shadow hover:bg-blue-50 transition"
            >
              View Projects
            </motion.a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;
